/**
 * Custom hooks for pledge actions with cache invalidation and notifications
 * Provides high-level mutation operations with optimistic updates
 *
 * Requirements:
 * - 6.1: Pledge authentication
 * - 6.2: Pledge amount validation
 * - 6.3: Pledge creation
 * - 6.4: Pledge record association
 * - 6.5: Funds increment
 */
import { mutate } from "swr";
import { useServerState, handleApiError } from "@project-mgmt/api-client";
/**
 * Helper function to invalidate pledge-related cache
 * Invalidates pledge lists and venture funding data
 *
 * @param ventureId - The venture ID to invalidate cache for
 * @param workspaceId - Optional workspace ID for invalidating workspace-specific pledge cache
 */
export function invalidatePledgeCache(ventureId, workspaceId) {
    // Invalidate venture pledges list using the correct cache key format
    mutate((key) => {
        if (typeof key !== "string")
            return false;
        try {
            const parsedKey = JSON.parse(key);
            if (!Array.isArray(parsedKey))
                return false;
            // Match ["ventures", ventureId, "pledges", workspaceId] pattern
            return parsedKey[0] === "ventures" && parsedKey[1] === ventureId && parsedKey[2] === "pledges";
        }
        catch {
            return false;
        }
    }, undefined, { revalidate: true });
    // Invalidate user's pledges list
    mutate("/api/users/me/pledges", undefined, { revalidate: true });
    // Invalidate the venture detail (to update funding progress)
    mutate((key) => {
        if (typeof key !== "string")
            return false;
        try {
            const parsedKey = JSON.parse(key);
            if (!Array.isArray(parsedKey))
                return false;
            // Match ["ventures", ventureId, workspaceId] pattern
            return parsedKey[0] === "ventures" && parsedKey[1] === ventureId && parsedKey.length === 3;
        }
        catch {
            return false;
        }
    }, undefined, { revalidate: true });
}
/**
 * Helper function to invalidate all venture lists after a pledge
 * This ensures funding progress is updated across all venture displays
 *
 * @param workspaceId - Optional workspace ID to invalidate workspace ventures
 */
export function invalidateVentureListsCache(workspaceId) {
    // Invalidate public ventures cache
    mutate((key) => {
        if (typeof key !== "string")
            return false;
        try {
            const parsedKey = JSON.parse(key);
            if (!Array.isArray(parsedKey))
                return false;
            // Match public ventures endpoints
            return parsedKey[0] === "/api/public/ventures" || parsedKey[0] === "/api/public/ventures/featured";
        }
        catch {
            return false;
        }
    }, undefined, { revalidate: true });
    // Invalidate workspace ventures if workspace ID provided
    if (workspaceId) {
        mutate((key) => {
            if (typeof key !== "string")
                return false;
            try {
                const parsedKey = JSON.parse(key);
                if (!Array.isArray(parsedKey))
                    return false;
                // Match workspace ventures endpoint
                return parsedKey[0] === `/api/ventures/workspace/${workspaceId}`;
            }
            catch {
                return false;
            }
        }, undefined, { revalidate: true });
    }
    // Invalidate user's ventures
    mutate((key) => {
        if (typeof key !== "string")
            return false;
        try {
            const parsedKey = JSON.parse(key);
            if (!Array.isArray(parsedKey))
                return false;
            return parsedKey[0] === "/api/users/me/ventures";
        }
        catch {
            return false;
        }
    }, undefined, { revalidate: true });
}
/**
 * Hook to create a pledge mutation
 * Requirement 6.1, 6.2, 6.3: Pledge creation with validation
 *
 * @param ventureId - The venture ID to pledge to
 * @param workspaceId - The workspace ID for authentication
 * @returns Mutation hook for creating pledges
 */
export function useCreatePledgeMutation(ventureId, workspaceId) {
    return useServerState({
        key: ["pledges:create", ventureId],
        fetcher: async (input, signal) => {
            try {
                const headers = {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
                };
                // Add workspace ID header if provided
                if (workspaceId) {
                    headers["X-Workspace-ID"] = workspaceId;
                }
                const response = await fetch(`/api/ventures/${ventureId}/pledge`, {
                    method: "POST",
                    headers,
                    body: JSON.stringify({
                        ventureId,
                        amount: input.amount,
                    }),
                    signal,
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    const error = new Error(errorData?.error || "Failed to create pledge");
                    error.status = response.status;
                    throw error;
                }
                return response.json();
            }
            catch (error) {
                throw handleApiError(error);
            }
        },
        manual: true,
        onError: (error) => {
            console.error("Failed to create pledge:", error);
        },
    });
}
/**
 * Hook for pledge actions
 * Provides create pledge operation with cache management and optimistic updates
 *
 * @param ventureId - The venture ID to pledge to
 * @param workspaceId - Optional workspace ID for cache invalidation
 * @param notify - Optional notification adapter for success/error messages
 * @returns Object with pledge creation function and loading state
 *
 * @example
 * ```tsx
 * const { createPledge, isCreating } = usePledgeActions(ventureId, workspaceId, {
 *   success: (msg) => toast.success(msg),
 *   error: (msg) => toast.error(msg),
 * });
 *
 * await createPledge(100); // Pledge $100
 * ```
 */
export function usePledgeActions(ventureId, workspaceId, notify) {
    const createPledgeMutation = useCreatePledgeMutation(ventureId, workspaceId);
    /**
     * Create a new pledge
     * Requirement 6.1, 6.2, 6.3, 6.4, 6.5: Pledge creation with validation and funds update
     */
    const createPledge = async (amount) => {
        try {
            // Optimistic update: immediately update the venture's funding progress
            mutate((key) => {
                if (typeof key !== "string")
                    return false;
                try {
                    const parsedKey = JSON.parse(key);
                    if (!Array.isArray(parsedKey))
                        return false;
                    // Match ["ventures", ventureId, workspaceId] pattern
                    return parsedKey[0] === "ventures" && parsedKey[1] === ventureId && parsedKey.length === 3;
                }
                catch {
                    return false;
                }
            }, async (currentVenture) => {
                if (!currentVenture)
                    return currentVenture;
                // Optimistically increment funds pledged
                return {
                    ...currentVenture,
                    fundsPledged: (currentVenture.fundsPledged || 0) + amount,
                    fundingProgress: Math.min(100, (((currentVenture.fundsPledged || 0) + amount) / currentVenture.targetGoal) * 100),
                };
            }, false // Don't revalidate yet
            );
            // Create the pledge
            const result = await createPledgeMutation.trigger({ amount });
            // Invalidate all relevant caches after successful pledge
            invalidatePledgeCache(ventureId, workspaceId);
            invalidateVentureListsCache(workspaceId);
            // Show success notification
            notify?.success("Pledge submitted successfully");
            return { success: true, data: result };
        }
        catch (error) {
            // Revert optimistic update on error
            mutate((key) => {
                if (typeof key !== "string")
                    return false;
                try {
                    const parsedKey = JSON.parse(key);
                    if (!Array.isArray(parsedKey))
                        return false;
                    // Match ["ventures", ventureId, workspaceId] pattern
                    return parsedKey[0] === "ventures" && parsedKey[1] === ventureId && parsedKey.length === 3;
                }
                catch {
                    return false;
                }
            }, undefined, { revalidate: true });
            const message = error instanceof Error ? error.message : "Failed to create pledge";
            // Show error notification
            notify?.error(message);
            return {
                success: false,
                error: message,
            };
        }
    };
    return {
        createPledge,
        isCreating: createPledgeMutation.isLoading,
    };
}
